package com.advaita.cloud.drive.ui.store

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.advaita.cloud.drive.shared.MeAccessDto
import com.advaita.cloud.drive.shared.MyTeamDto
import com.advaita.cloud.drive.ui.api.FilesApi
import com.advaita.cloud.drive.ui.api.RedirectingException
import com.advaita.cloud.drive.ui.api.ShareApiException
import com.advaita.cloud.drive.ui.components.capForSharedList
import com.advaita.cloud.drive.ui.components.capSentence
import kotlinx.coroutines.CancellationException

interface AccessBackend {
    suspend fun access(): MeAccessDto
}

object HttpAccessBackend : AccessBackend {
    override suspend fun access() = FilesApi.fetchMeAccess()
}

/** Shown when My access cannot load for a reason that is not a cap. */
const val ACCESS_FAILED_SENTENCE = "Your access details are unavailable right now."

/** The vault status as one sentence. Null means the vault holds no record for the person in DataHub. */
fun statusSentence(status: String?): String = when (status) {
    "ADMITTED" -> "Admitted"
    "PENDING" -> "Waiting for an org admin to approve you."
    "DENIED" -> "Denied"
    "REMOVED" -> "Removed"
    else -> "Not yet used"
}

/** The role copy of the spec table "Role copy" (D13). Another role shows its name with a capital letter. */
fun roleLabel(baseRole: String?): String = when (baseRole) {
    null -> "None"
    "member" -> "Member"
    "guest" -> "Guest"
    else -> baseRole.replaceFirstChar { it.uppercase() }
}

fun roleHint(baseRole: String?): String? = when (baseRole) {
    "member" -> "Uses DataHub fully and can see org-wide files"
    "guest" -> "Sees only what's shared with them or their teams"
    else -> null
}

fun teamChip(t: MyTeamDto): String = if (t.lead) "${t.name} · Lead" else t.name

fun orgAdminText(orgAdmin: Boolean): String = if (orgAdmin) "Yes" else "No"

/**
 * CR-6.5 and CR-6.6 (D9, D13): the state of the account menu and the My access dialog. DOM-free.
 * [info] is null until `GET /api/me/access` answers, and after a failure. [consoleUrl] is the
 * backend's decision: it is set only for an org admin or a team lead.
 */
class MyAccessStore(private val backend: AccessBackend = HttpAccessBackend) {
    var info: MeAccessDto? by mutableStateOf(null)
        private set
    var loading: Boolean by mutableStateOf(false)
        private set
    var failure: String? by mutableStateOf(null)
        private set
    var menuOpen: Boolean by mutableStateOf(false)
        private set
    var dialogOpen: Boolean by mutableStateOf(false)
        private set

    val consoleUrl: String? get() = info?.consoleUrl

    suspend fun load() {
        loading = true
        try {
            info = backend.access()
            failure = null
        } catch (e: CancellationException) {
            throw e
        } catch (e: RedirectingException) {
            throw e
        } catch (e: ShareApiException) {
            info = null
            failure = capForSharedList(e)?.let { capSentence(it) } ?: ACCESS_FAILED_SENTENCE
        } catch (t: Throwable) {
            info = null
            failure = ACCESS_FAILED_SENTENCE
        } finally {
            loading = false
        }
    }

    fun toggleMenu() { menuOpen = !menuOpen }
    fun closeMenu() { menuOpen = false }
    fun openDialog() { menuOpen = false; dialogOpen = true }
    fun closeDialog() { dialogOpen = false }
}
